import { ArrowUp, Check, HeartHandshake, MessageCircleQuestion } from 'lucide-react-native';
import { memo, useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { twJoin } from 'tailwind-merge';

import { AnimatedCheck } from '@/components/animatedCheck';
import { Box } from '@/components/ui/box';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { DURATION } from '@/constants/animation';
import { MOTION } from '@/constants/motion';
import {
  ON_YOUR_HONOR_COPY,
  ON_YOUR_HONOR_REASONS,
  type OnYourHonorReasonId,
} from '../../model/onYourHonor';
import { useColors } from '@/hooks/useColors';
import { useHaptic } from '@/hooks/useHaptic';
import { usePressAnimation } from '@/hooks/usePressAnimation';
import { useSuccessCheckAnimation } from '@/hooks/useSuccessCheckAnimation';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP, TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

/**
 * How long the check is held before the thank-you replaces it, counted from the
 * beat landing rather than from the check settling.
 *
 * {@link useSuccessCheckAnimation} spends roughly the first 500ms of this
 * arriving: an 180ms fade, then a 220ms overshoot and its spring. A hold sized
 * as if the check were static therefore shows a check that is still moving when
 * it starts to leave, which reads as a flicker rather than a confirmation. This
 * leaves about 900ms of stillness after it settles.
 */
const CONFIRM_HOLD_MS = 1400;

/**
 * Delay before the check starts drawing, so the disc has landed under it first.
 * Drawing into a disc that is still overshooting reads as two things competing
 * rather than one arriving.
 */
const CHECK_DRAW_DELAY_MS = 260;

/**
 * How long the thank-you is held before the surface closes, counted from the
 * moment it replaces the check, so only its own fade comes out of it.
 *
 * Sized for the longest line in the rotation, not the average: which line an
 * expert gets is random, so a hold fitted to the short one would close
 * mid-sentence on a quarter of marks. Bespoke to this beat, hence a local value
 * rather than one of the shared `DURATION` tokens, which cover transitions.
 */
const THANKS_HOLD_MS = 2500;

/**
 * Chip colors aligned to the "On your honor" toggle so the survey reads as the
 * same control family. Both are the secondary (slate) ramp: outlined when
 * unselected, filled when checked.
 *
 * Every shade is pinned across hover / active / both themes because the cursor
 * sits on the chip right after the click — without pinning, the button's default
 * hover fill (gray) and solid text (`typography-800`, dark in dark mode) win, so
 * the checked chip renders gray with dark-on-dark text. The fill resolves to the
 * same mid-slate (#475f6b) in both themes (`secondary-600` light / `-300` dark),
 * with white text that swaps to `typography-950` in dark, matching the toggle.
 */
const CHIP_OUTLINE = 'border-secondary-400 data-[hover=true]:border-secondary-500';

const CHIP_OUTLINE_TEXT =
  'text-secondary-600 data-[hover=true]:text-secondary-700 data-[active=true]:text-secondary-800';

const CHECKED_FILL =
  'border-secondary-600 bg-secondary-600 data-[hover=true]:border-secondary-600 data-[hover=true]:bg-secondary-600 data-[active=true]:border-secondary-600 data-[active=true]:bg-secondary-600 dark:border-secondary-300 dark:bg-secondary-300 dark:data-[hover=true]:border-secondary-300 dark:data-[hover=true]:bg-secondary-300 dark:data-[active=true]:border-secondary-300 dark:data-[active=true]:bg-secondary-300';

const CHECKED_TEXT =
  'text-typography-white data-[hover=true]:text-typography-white data-[active=true]:text-typography-white dark:text-typography-950 dark:data-[hover=true]:text-typography-950 dark:data-[active=true]:text-typography-950';

// Pill-shaped, signalling selectable tokens/tags distinct from action buttons
// (the Material / Uber-DoorDash convention for this quick-select pattern).
const CHIP = `${TOUCH_TARGET_MIN_H} rounded-full px-4`;

/**
 * Press-down for the reason pills. Deeper than {@link MOTION.press.standard},
 * whose 0.985 is calibrated for full-width CTAs and moves a content-sized pill
 * by about a pixel. The same override MOD makes for its text link, for the same
 * reason: the scale has to be legible against the element's own size.
 */
const CHIP_PRESS = { pressedScale: 0.96, spring: MOTION.press.standard.spring };

/**
 * The badge slot, held at one size and position through every state so the sheet
 * never introduces or removes it. Only what sits inside changes, which is what
 * lets the surface read as asking, then recorded, then thanked.
 */
const BADGE = 'mb-3 h-14 w-14 items-center justify-center self-center rounded-full';

type ReasonChipProps = {
  label: string;
  /** Whether this chip is the one that was chosen, driving the filled state. */
  isConfirming: boolean;
  onPress: () => void;
};

/**
 * The badge in its recorded state: a check drawing itself in the same disc the
 * question sat in. The container does not change; the glyph, the spring and the
 * draw carry the confirmation between them.
 *
 * A component rather than inline markup so it mounts when the reason lands,
 * which is what starts both entrances. Inline, the hooks would belong to the
 * sheet and would have run on its mount, long before the tap.
 *
 * The stroke reads from `useColors` because an SVG stroke needs a resolved value
 * rather than a class, and `primary-600` differs by theme.
 */
function RecordedBadge() {
  const entrance = useSuccessCheckAnimation(0);

  const { colors } = useColors();

  return (
    <Animated.View style={entrance}>
      <Box className={twJoin(BADGE, 'bg-primary-50')}>
        <AnimatedCheck size={30} color={colors.primary[600]} delay={CHECK_DRAW_DELAY_MS} />
      </Box>
    </Animated.View>
  );
}

/**
 * One reason pill. Split out so each can own its press animation: the hook holds
 * a shared value per chip, and hooks cannot be called from the map that renders
 * them.
 */
const ReasonChip = memo(function ReasonChip({ label, isConfirming, onPress }: ReasonChipProps) {
  const press = usePressAnimation(CHIP_PRESS);

  return (
    <Animated.View style={press.animatedStyle}>
      <Button
        size="sm"
        action="secondary"
        variant={isConfirming ? 'solid' : 'outline'}
        onPress={onPress}
        onPressIn={press.onPressIn}
        onPressOut={press.onPressOut}
        // Selected = solid fill only (no check icon): the fill signals it without
        // changing width, so a tap never reflows the row, and pills stay content-sized.
        className={twJoin(CHIP, isConfirming ? CHECKED_FILL : CHIP_OUTLINE)}
        accessibilityState={{ selected: isConfirming }}
        {...getAccessibilityProps({ label, role: 'button' })}>
        <ButtonText
          className={twJoin('text-base', isConfirming ? CHECKED_TEXT : CHIP_OUTLINE_TEXT)}>
          {label}
        </ButtonText>
      </Button>
    </Animated.View>
  );
});

type OnYourHonorReasonContentProps = {
  /**
   * Rotating gratitude line for the answered state, pre-composed by the caller.
   * Null while the question is still open; setting it is what swaps the body to
   * the thank-you, so the caller controls that transition by applying the mark
   * and handing back the line it produced.
   */
  thanksLine: string | null;
  /** Record a predefined effort reason (single tap on a chip). */
  onSelectReason: (id: OnYourHonorReasonId) => void;
  /** Record a free-text reason the expert typed via "Other". */
  onCustomReason: (text: string) => void;
  /** The thank-you beat has been read; close the surface. */
  onThanksComplete: () => void;
};

/**
 * Body of the "above and beyond" reason survey, shared by the native bottom
 * sheet and the web modal. Two states in one surface.
 *
 * **Asking.** A row of single-tap chips answering what made the answer take
 * extra work. This is effort capture, not a rating: the question is about what
 * the expert did, deliberately not how good the answer was. A free-text field
 * sits below the chips and is always visible — the "something else" path, kept
 * always-on so the surface never resizes or reflows (a toggled field would grow
 * a centered modal and make it jump). Escape is the host's X alone: a second,
 * in-body exit is a Skip control however it is worded, and answering is the only
 * path that marks the inquiry.
 *
 * **Thanking.** Once the caller applies the mark it hands back a gratitude line
 * and the body swaps to it for {@link THANKS_HOLD_MS} before closing. The thanks
 * lands here rather than in a toast because the expert is already looking at this
 * surface. Marks that never open the sheet still get a toast: see
 * `useOnYourHonorReasonFlow`.
 */
export const OnYourHonorReasonContent = memo(function OnYourHonorReasonContent({
  thanksLine,
  onSelectReason,
  onCustomReason,
  onThanksComplete,
}: OnYourHonorReasonContentProps) {
  const [customText, setCustomText] = useState('');

  /** Id (or 'custom') of the choice being acknowledged; drives the confirm state. */
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);

  const haptic = useHaptic();

  const isThanking = thanksLine !== null;

  /**
   * Which beat is on screen. Trails the mark, so the swap happens under cover of
   * a fade rather than as a visible cut.
   *
   * There are only two. A reason landing does not change the screen: the badge
   * turns into a check where the question still stands, so the confirmation
   * happens in place rather than by replacing everything the expert was looking
   * at. Only the move to gratitude is a change of screen.
   *
   * Driven by a shared value rather than reanimated's `entering`/`exiting` layout
   * animations: those do not fire under react-native-web here, so the transition
   * was invisible on web. It also matches the house pattern — `useAnimatedStyle`
   * over a shared value, honouring `useReducedMotion`, the way
   * `useEntranceAnimation` and the extracted-questions components do.
   */
  const [beat, setBeat] = useState<'ask' | 'thanks'>('ask');

  /** The reason has landed and the check is showing over the question. */
  const isRecorded = isThanking && beat === 'ask';

  const opacity = useSharedValue(1);

  const reducedMotion = useReducedMotion();

  const fadeStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));

  /** Press-down for the write-in send button, matching the chips beside it. */
  const sendPress = usePressAnimation(CHIP_PRESS);

  /** Fades the body out, then swaps to the thank-you behind the blank. */
  const advance = useCallback(() => {
    if (reducedMotion) {
      setBeat('thanks');

      return;
    }

    opacity.value = withTiming(0, { duration: DURATION.fast }, (finished) => {
      if (finished) {
        runOnJS(setBeat)('thanks');
      }
    });
  }, [opacity, reducedMotion]);

  /** Reopened for a fresh mark: back to the question at full opacity. */
  useEffect(() => {
    if (!isThanking && beat === 'thanks') {
      setBeat('ask');

      opacity.value = 1;
    }
  }, [isThanking, beat, opacity]);

  /** Fades each landed beat in. */
  useEffect(() => {
    if (beat === 'ask') {
      return;
    }

    opacity.value = reducedMotion ? 1 : withTiming(1, { duration: DURATION.normal });
  }, [beat, opacity, reducedMotion]);

  /** Holds the check over the question, then moves to the thank-you. */
  useEffect(() => {
    if (!isRecorded) {
      return;
    }

    const timer = setTimeout(advance, CONFIRM_HOLD_MS);

    return () => clearTimeout(timer);
  }, [isRecorded, advance]);

  /**
   * Holds the thank-you, then closes. The cleanup cancels the timer when the
   * expert closes early, which would otherwise fire `onThanksComplete` against a
   * dismissed surface.
   */
  useEffect(() => {
    if (beat !== 'thanks') {
      return;
    }

    const timer = setTimeout(onThanksComplete, THANKS_HOLD_MS);

    return () => clearTimeout(timer);
  }, [beat, onThanksComplete]);

  /**
   * Fills the chosen chip, fires a light haptic and commits, all on the tap.
   *
   * There used to be a hold here so the fill could be seen before the surface
   * closed. The thank-you replaced that: it is now the acknowledgement, and it
   * arrives on its own beat, so the hold was pure latency between the tap and any
   * response. Guarded so a second tap cannot commit twice.
   */
  const confirmThenCommit = (key: string, commit: () => void) => {
    if (confirmingKey) {
      return;
    }

    setConfirmingKey(key);

    haptic.selection();

    commit();
  };

  const submitCustom = () => {
    const trimmed = customText.trim();

    if (!trimmed) {
      return;
    }

    confirmThenCommit('custom', () => {
      setCustomText('');

      onCustomReason(trimmed);
    });
  };

  /**
   * The badge for the state the sheet is in. One slot, three meanings, so the
   * surface reads as updating rather than being replaced.
   *
   * The disc holds one color through the exchange and changes once, for the
   * thanks: green while asking and recording, peach when the sheet stops asking
   * for something and starts giving it. A status badge should keep its container
   * and let the glyph carry the state; recording already announces itself
   * through the swap, the spring and the drawing stroke, so the background has
   * nothing left to say.
   *
   * `primary-50` rather than `primary-100`, which at 1.53 against white is
   * heavier than everything else on the beat where the chips are the thing to
   * use. At 1.19 it sits level with the peach, so no state outweighs another.
   *
   * Green rather than the semantic `success` ramp: nothing succeeded or failed,
   * the expert answered a question. And not `secondary`, the control color
   * here — a blue disc would group the badge with the things you press.
   */
  const badge = () => {
    if (beat === 'thanks') {
      return (
        <Box className={twJoin(BADGE, 'bg-tertiary-100')}>
          <Icon as={HeartHandshake} className="h-7 w-7 text-tertiary-600" />
        </Box>
      );
    }

    if (isRecorded) {
      return <RecordedBadge />;
    }

    return (
      <Box className={twJoin(BADGE, 'bg-primary-50')}>
        <Icon as={MessageCircleQuestion} className="h-7 w-7 text-primary-600" />
      </Box>
    );
  };

  /**
   * One layout for every state. Only the badge and the heading change; the chips
   * and the write-in stay put throughout, so the sheet never resizes, the mark
   * never moves, and the gratitude does not arrive on an almost-empty screen.
   *
   * The chips are left interactive rather than disabled because
   * {@link confirmThenCommit} already ignores a second choice, so nothing needs
   * to guard them and dimming would only draw attention to controls that have
   * finished their job.
   *
   * The supporting line is rendered but invisible once thanked: it has nothing
   * to say next to gratitude, and removing it outright would pull the chips up
   * by its height at the moment everything else is holding still.
   */
  const isThanked = beat === 'thanks';

  const question = (
    <View className="w-full">
      <VStack className="w-full gap-1">
        <Animated.View style={fadeStyle}>
          {badge()}

          <Text className="text-center font-heading text-lg text-typography-900">
            {isThanked ? thanksLine : ON_YOUR_HONOR_COPY.reasonPrompt}
          </Text>

          <Text
            className={twJoin(
              'mb-3 mt-0.5 text-center text-sm text-typography-700',
              isThanked ? 'opacity-0' : ''
            )}
            accessibilityElementsHidden={isThanked}
            importantForAccessibility={isThanked ? 'no-hide-descendants' : 'auto'}>
            {ON_YOUR_HONOR_COPY.reasonWhy}
          </Text>
        </Animated.View>

        <VStack className="w-full gap-3">
          <HStack className="flex-wrap items-center justify-center gap-2.5">
            {ON_YOUR_HONOR_REASONS.map((reason) => (
              <ReasonChip
                key={reason.id}
                label={reason.label}
                isConfirming={confirmingKey === reason.id}
                onPress={() => confirmThenCommit(reason.id, () => onSelectReason(reason.id))}
              />
            ))}
          </HStack>

          <Box className="relative w-full">
            <Input
              variant="outline"
              size="md"
              className="w-full rounded-full bg-background-50 data-[focus=true]:border-secondary-500 data-[focus=true]:web:ring-0">
              <InputField
                value={customText}
                onChangeText={setCustomText}
                onSubmitEditing={submitCustom}
                returnKeyType="done"
                placeholder={ON_YOUR_HONOR_COPY.reasonCustomPlaceholder}
                className="pr-12 text-base text-typography-900"
                {...getAccessibilityProps({ label: ON_YOUR_HONOR_COPY.reasonCustomPlaceholder })}
              />
            </Input>

            {/* Solid send button inside the field, revealed once there's text. Slate
              mirrors the checked chip so the controls read as one family, and it
              confirms in the same brand green the badge turns, so one action does
              not put two greens on screen. */}
            {customText.trim() ? (
              <Pressable
                onPress={submitCustom}
                onPressIn={sendPress.onPressIn}
                onPressOut={sendPress.onPressOut}
                hitSlop={ICON_BUTTON_HIT_SLOP}
                className="absolute bottom-0 right-1.5 top-0 justify-center active:opacity-80 data-[hover=true]:opacity-90"
                {...getAccessibilityProps({
                  role: 'button',
                  label: ON_YOUR_HONOR_COPY.reasonCustomSubmitA11yLabel,
                })}>
                <Animated.View style={sendPress.animatedStyle}>
                  <Box
                    className={twJoin(
                      'h-8 w-8 items-center justify-center rounded-full',
                      confirmingKey === 'custom'
                        ? 'bg-primary-500'
                        : 'bg-secondary-600 dark:bg-secondary-300'
                    )}>
                    <Icon
                      as={confirmingKey === 'custom' ? Check : ArrowUp}
                      size="sm"
                      className={
                        confirmingKey === 'custom'
                          ? 'text-typography-white'
                          : 'text-typography-white dark:text-typography-950'
                      }
                    />
                  </Box>
                </Animated.View>
              </Pressable>
            ) : null}
          </Box>
        </VStack>
      </VStack>
    </View>
  );

  return question;
});

OnYourHonorReasonContent.displayName = 'OnYourHonorReasonContent';
